/**
 * Nettoyage des URLs avant envoi aux outils de mesure d'audience.
 *
 * SÉCURITÉ (audit 2026-09-10) — fuite de jeton vers un tiers.
 *
 * `GA4PageTracker` envoyait `page_path = pathname + query string COMPLÈTE` et
 * `page_location = window.location.href`, sans aucun filtrage. Or le lien de
 * réinitialisation de mot de passe est de la forme
 * `/mot-de-passe-reinit?token=<JWT>` (cf.
 * src/app/api/auth/password-reset-request/route.ts). Ouvrir ce lien expédiait
 * donc le jeton de réinitialisation à Google Analytics : toute personne ayant
 * accès à la propriété GA4 pouvait relever ce jeton et changer le mot de passe
 * du compte visé, tant que le jeton n'avait pas expiré.
 *
 * PostHog filtrait déjà six paramètres de son côté (`sanitize_properties` dans
 * PostHogProvider.tsx) — GA4 non. Les deux partagent désormais cette liste,
 * pour qu'un ajout profite aux deux et qu'on ne puisse plus en durcir un seul.
 *
 * Principe : liste NOIRE de noms de paramètres, appliquée sans distinction de
 * casse. Une liste blanche serait plus sûre en théorie, mais casserait le suivi
 * des campagnes (utm_*, gclid, fbclid…) et de la pagination, qui sont
 * précisément ce qu'on veut mesurer.
 */

/**
 * Paramètres retirés de toute URL transmise à un outil de mesure.
 *
 * Ajouter ici tout paramètre qui transporte un secret, une identité, ou une
 * donnée de santé. Dans le doute, ajouter : perdre une dimension d'analyse
 * coûte moins cher que fuiter un jeton.
 */
export const ANALYTICS_STRIPPED_PARAMS = [
  // Secrets et jetons
  "token",
  "otp",
  "code",
  "secret",
  "key",
  "auth",
  "access_token",
  "id_token",
  "refresh_token",
  "challengetoken",
  "state",
  "signature",
  "sig",
  // Identité directe — ne doit jamais partir chez un tiers
  "email",
  "phone",
  "tel",
  // Destination de redirection : peut contenir un chemin authentifié
  "returnto",
] as const;

const STRIPPED = new Set<string>(ANALYTICS_STRIPPED_PARAMS.map((p) => p.toLowerCase()));

/**
 * Retire les paramètres sensibles d'une URL absolue ou d'un chemin relatif.
 *
 * Accepte les deux formes parce que GA4 reçoit `page_location` (URL absolue)
 * ET `page_path` (chemin + query). Toute valeur inanalysable est renvoyée
 * amputée de sa query : mieux vaut perdre la mesure que laisser passer un
 * jeton par un format inattendu.
 */
export function sanitizeAnalyticsUrl(input: string): string {
  if (!input) return input;

  // Aucune query : rien à filtrer.
  const qIndex = input.indexOf("?");
  if (qIndex === -1) return input;

  try {
    // Base factice : permet de traiter chemin relatif et URL absolue de la
    // même façon. Elle n'est réinjectée dans la sortie que si l'entrée était
    // elle-même absolue.
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(input);
    const url = new URL(input, "https://sahha.ma");

    for (const name of [...url.searchParams.keys()]) {
      if (STRIPPED.has(name.toLowerCase())) url.searchParams.delete(name);
    }

    if (isAbsolute) return url.toString();
    return url.pathname + (url.search || "") + (url.hash || "");
  } catch {
    // Format inattendu → on coupe toute la query plutôt que de la laisser fuir.
    return input.slice(0, qIndex);
  }
}
